import { apm } from "@elastic/apm-rum";
import { mm } from "@inovitas/infra3dsdk";
import { ChevronLeft, Close, ExpandMore, ZoomInMap } from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import measurement_empty_state from "shared/assets/measurementEmptyState.svg";
import UseAuth from "shared/common/hooks/UseAuth";
import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import measurementTools, {
  MeasurementType,
} from "shared/common/utils/measurements/measurementTools";

export default function MeasurementContent() {
  const { t } = useTranslation();

  const viewer = defaultStore((state) => state.viewer);
  const darkTheme = defaultStore((state) => state.darkTheme);
  const loading = defaultStore((state) => state.loading);
  const activeCampaign = defaultStore((state) => state.activeCampaign);
  const axisCollection = defaultStore((state) => state.axisCollection);

  const measurementActiveTool = sidebarStore(
    (state) => state.measurementActiveTool
  );
  const changeMeasurementActiveTool = sidebarStore(
    (state) => state.changeMeasurementActiveTool
  );
  const lastMeasurementActiveTool = sidebarStore(
    (state) => state.lastMeasurementActiveTool
  );
  const changeLastMeasurementActiveTool = sidebarStore(
    (state) => state.changeLastMeasurementActiveTool
  );
  const showDescription = sidebarStore((state) => state.showDescription);
  const changeShowDescription = sidebarStore(
    (state) => state.changeShowDescription
  );
  const changeShowSmallToolContent = sidebarStore(
    (state) => state.changeShowSmallToolContent
  );
  const changeSmallToolContent = sidebarStore(
    (state) => state.changeSmallToolContent
  );
  const maximizeTable = sidebarStore((state) => state.maximizeTable);
  const changeMaximizeTable = sidebarStore(
    (state) => state.changeMaximizeTable
  );
  const changeVerticalDistanceToolValues = sidebarStore(
    (state) => state.changeVerticalDistanceToolValues
  );
  const changeOrthogonalDistanceToolValues = sidebarStore(
    (state) => state.changeOrthogonalDistanceToolValues
  );
  const orthogonalDistanceActiveAttributes = sidebarStore(
    (state) => state.orthogonalDistanceActiveAttributes
  );
  const changeDistanceToolValues = sidebarStore(
    (state) => state.changeDistanceToolValues
  );
  const changeAreaToolValues = sidebarStore(
    (state) => state.changeAreaToolValues
  );
  const changeCoordinateToolValues = sidebarStore(
    (state) => state.changeCoordinateToolValues
  );
  const changeProfileToolValues = sidebarStore(
    (state) => state.changeProfileToolValues
  );
  const changeLinrefToolValues = sidebarStore(
    (state) => state.changeLinrefToolValues
  );
  const distanceActiveAttributes = sidebarStore(
    (state) => state.distanceActiveAttributes
  );
  const verticalDistanceActiveAttributes = sidebarStore(
    (state) => state.verticalDistanceActiveAttributes
  );
  const areaActiveAttributes = sidebarStore(
    (state) => state.areaActiveAttributes
  );
  const coordinatesActiveAttributes = sidebarStore(
    (state) => state.coordinatesActiveAttributes
  );
  const surveyWheelActiveAttributes = sidebarStore(
    (state) => state.surveyWheelActiveAttributes
  );
  const linrefActiveAttributes = sidebarStore(
    (state) => state.linrefActiveAttributes
  );
  const coordinateTool = sidebarStore((state) => state.coordinateTool);
  const distanceTool = sidebarStore((state) => state.distanceTool);
  const areaTool = sidebarStore((state) => state.areaTool);
  const verticalDistanceTool = sidebarStore(
    (state) => state.verticalDistanceTool
  );
  const profileTool = sidebarStore((state) => state.profileTool);
  const orthogonalDistanceTool = sidebarStore(
    (state) => state.orthogonalDistanceTool
  );
  const linrefTool = sidebarStore((state) => state.linrefTool);
  const closeAllToolPopover = sidebarStore(
    (state) => state.closeAllToolPopover
  );
  const surveyWheelTool = sidebarStore((state) => state.surveyWheelTool);

  const { userData } = UseAuth();

  const [canShowSmallWindow, setCanShowSmallWindow] = useState<boolean>(false);

  const [canMeasure, setCanMeasure] = useState<boolean>(false);

  const tools: MeasurementType[] = measurementTools();

  //close measurement popover
  const handleCloseMeasurement = () => {
    closeAllToolPopover();
  };

  //activate selected measure tool
  const activateTool = (tool: MeasurementType) => {
    if (measurementActiveTool?.id == tool.id) {
      changeMeasurementActiveTool(undefined);
      changeLastMeasurementActiveTool(undefined);
      return;
    }
    changeMeasurementActiveTool(tool);
    changeLastMeasurementActiveTool(tool);
  };

  //show or hide description
  const handleDescription = () => {
    changeShowDescription(!showDescription);
  };

  //show small tool content
  const handleToolSmallWindow = () => {
    handleCloseMeasurement();
    changeShowSmallToolContent(true);
  };

  //change small tool content based on active measurement tool
  useEffect(() => {
    if (measurementActiveTool) {
      changeSmallToolContent(measurementActiveTool);
    } else {
      changeSmallToolContent(undefined);
    }
    enableOrDisableTools(measurementActiveTool);
  }, [measurementActiveTool]);

  //check if should open or not small tool
  useEffect(() => {
    checkSmallWindow(measurementActiveTool);
  }, [
    measurementActiveTool,
    areaActiveAttributes,
    distanceActiveAttributes,
    verticalDistanceActiveAttributes,
    coordinatesActiveAttributes,
    linrefActiveAttributes,
  ]);

  //check if should open or not small tool
  const checkSmallWindow = (tool: MeasurementType | undefined) => {
    if (tools.length > 0 && tool && tool?.id !== "profile") {
      switch (tool.id) {
        case "measureArea":
          if (areaActiveAttributes.length > 0) {
            setCanShowSmallWindow(true);
          } else {
            setCanShowSmallWindow(false);
          }
          return;
        case "distance":
          if (distanceActiveAttributes.length > 0) {
            setCanShowSmallWindow(true);
          } else {
            setCanShowSmallWindow(false);
          }
          return;
        case "surveyWheel":
          if (surveyWheelActiveAttributes.length > 0) {
            setCanShowSmallWindow(true);
          } else {
            setCanShowSmallWindow(false);
          }
          return;
        case "verticalDistance":
          if (verticalDistanceActiveAttributes.length > 0) {
            setCanShowSmallWindow(true);
          } else {
            setCanShowSmallWindow(false);
          }
          return;
        case "orthogonalDistance":
          if (orthogonalDistanceActiveAttributes.length > 0) {
            setCanShowSmallWindow(true);
          } else {
            setCanShowSmallWindow(false);
          }
          return;
        case "coordinate":
          if (coordinatesActiveAttributes.length > 0) {
            setCanShowSmallWindow(true);
          } else {
            setCanShowSmallWindow(false);
          }
          return;
        case "linref":
          if (linrefActiveAttributes.length > 0) {
            setCanShowSmallWindow(true);
          } else {
            setCanShowSmallWindow(false);
          }
          return;
        default:
          setCanShowSmallWindow(false);
      }
    } else {
      setCanShowSmallWindow(false);
    }
  };

  const enableOrDisableTools = (tool: MeasurementType | undefined) => {
    if (viewer !== undefined) {
      //----------- distance tool --------------------------------------
      if (tool && tool.id === "distance") {
        if (distanceTool && !distanceTool.enabled) distanceTool.enable();
      } else {
        if (distanceTool) distanceTool.disable();
        changeDistanceToolValues(undefined);
      }
      //----------- survey wheel tool --------------------------------------
      if (tool && tool.id === "surveyWheel") {
        if (surveyWheelTool && !surveyWheelTool.enabled)
          surveyWheelTool.enable();
      } else {
        if (surveyWheelTool) surveyWheelTool.disable();
        changeDistanceToolValues(undefined);
      }
      //----------------------------------------------------------------
      //----------- vertical distance ----------------------------------
      if (
        tool &&
        tool.id === "verticalDistance" &&
        !(measurementActiveTool?.isBlockedOnOldView && canMeasure)
      ) {
        if (verticalDistanceTool && !verticalDistanceTool.enabled)
          verticalDistanceTool.enable();
      } else {
        if (verticalDistanceTool) verticalDistanceTool.disable();
        changeVerticalDistanceToolValues(undefined);
      }
      //----------------------------------------------------------------
      //----------- area measurement ----------------------------------
      if (tool && tool.id === "measureArea") {
        if (areaTool && !areaTool.enabled) areaTool.enable();
      } else {
        if (areaTool) areaTool.disable();
        changeAreaToolValues(undefined);
      }
      //----------------------------------------------------------------
      //----------- coordinate position ----------------------------------
      if (tool && tool.id === "coordinate") {
        if (coordinateTool && !coordinateTool.enabled) coordinateTool.enable();
      } else {
        if (coordinateTool) coordinateTool.disable();
        changeCoordinateToolValues(undefined);
      }
      //----------------------------------------------------------------
      //----------- profile datas ----------------------------------
      if (
        tool &&
        tool.id === "profile" &&
        !(measurementActiveTool?.isBlockedOnOldView && canMeasure)
      ) {
        if (profileTool && !profileTool.enabled) profileTool.enable();
      } else {
        if (profileTool) profileTool.disable();
        changeProfileToolValues(undefined);
      }
      //----------------------------------------------------------------
      //----------- orthogonal distance ----------------------------------
      if (
        tool &&
        tool.id === "orthogonalDistance" &&
        !(measurementActiveTool?.isBlockedOnOldView && canMeasure)
      ) {
        if (orthogonalDistanceTool && !orthogonalDistanceTool.enabled)
          orthogonalDistanceTool.enable();
      } else {
        if (orthogonalDistanceTool) orthogonalDistanceTool.disable();
        changeOrthogonalDistanceToolValues(undefined);
      }
      //----------------------------------------------------------------
      //----------- linref measure ----------------------------------
      if (
        tool &&
        tool.id === "linref" &&
        !(measurementActiveTool?.isBlockedOnOldView && canMeasure)
      ) {
        if (linrefTool && !linrefTool.enabled) linrefTool.enable();
      } else {
        if (linrefTool) linrefTool.disable();
        changeLinrefToolValues(null);
      }
      //----------------------------------------------------------------
    }
  };

  //show active tool after popover has been opened
  useEffect(() => {
    if (lastMeasurementActiveTool) {
      changeMeasurementActiveTool(lastMeasurementActiveTool);
    }

    return () => {
      if (coordinateTool) coordinateTool.disable();
      if (distanceTool) distanceTool.disable();
      if (areaTool) areaTool.disable();
      if (verticalDistanceTool) verticalDistanceTool.disable();
      if (profileTool) profileTool.disable();
      if (orthogonalDistanceTool) orthogonalDistanceTool.disable();
      if (linrefTool) linrefTool.disable();
      if (surveyWheelTool) surveyWheelTool.disable();
    };
  }, []);

  //check measure mode if can measure or not
  const checkMeasureMode = () => {
    const measureMode = viewer
      ?.getScene()
      .getMeasureModeHandler()
      .getMeasureMode();
    if (
      (measureMode === mm.MEASUREMODE.MULTIVIEW ||
        measureMode === mm.MEASUREMODE.PANODEPTHMAPINTERSECTION ||
        measureMode ===
          mm.MEASUREMODE.PANODEPTHMAPINTERSECTION_MULTIVIEW_COMBINED ||
        measureMode === mm.MEASUREMODE.PANO_TWO_CLICK ||
        measureMode ===
          mm.MEASUREMODE.PANODEPTHMAPINTERSECTION_PANO_TWO_CLICK_COMBINED) &&
      verticalDistanceTool &&
      orthogonalDistanceTool &&
      profileTool &&
      surveyWheelTool
    ) {
      setCanMeasure(true);
      verticalDistanceTool.disable();
      orthogonalDistanceTool.disable();
      profileTool.disable();
      surveyWheelTool.disable();
    } else {
      setCanMeasure(false);
    }
  };

  //check measure mode
  useEffect(() => {
    if (viewer) {
      checkMeasureMode();
    }
  }, [activeCampaign, loading]);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          gap: 0.5,
          paddingX: 1.5,
          paddingY: 1.5,
          height: "100%",
          overflow: "hidden",
          backgroundColor: darkTheme ? "darkblue.main" : "white",
          borderRadius: "10px",
          transition: "height 0.3s , margin 0.3s , padding 0.3s",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 1,
            alignItems: "center",
            color: darkTheme ? "white" : "darkblue.main",
          }}
        >
          <Typography
            noWrap
            sx={{
              fontWeight: "600",
              marginRight: "auto",
              color: darkTheme ? "white" : "darkblue.main",
            }}
          >
            {t("measurement.title")}
          </Typography>
          {canShowSmallWindow && (
            <IconButton
              sx={{
                padding: 0,
                marginY: 1,
                color: darkTheme ? "white" : "darkblue.main",
                "@media (hover: hover)": {
                  ":hover": { color: "secondary.main" },
                },
              }}
              onClick={() =>
                monitor(
                  apm,
                  "arrow-small-window | Measure",
                  handleToolSmallWindow
                )
              }
              disableRipple={true}
            >
              <ChevronLeft />
            </IconButton>
          )}
          <IconButton
            sx={{
              padding: 0,
              marginY: 1,
              color: darkTheme ? "white" : "darkblue.main",
              "@media (hover: hover)": {
                ":hover": { color: "secondary.main" },
              },
            }}
            onClick={() =>
              monitor(
                apm,
                "close-measure-tool | Measure",
                handleCloseMeasurement
              )
            }
            disableRipple={true}
          >
            <Close />
          </IconButton>
        </Box>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexWrap: "wrap",
            marginBottom: 1,
            backgroundColor: darkTheme ? "primary.light" : "grey.light",
          }}
        >
          {tools.map((item) => {
            if (
              item.id === "linref" &&
              (!userData?.scope?.includes("addon/linref") ||
                axisCollection.length === 0)
            ) {
              return null;
            }
            return (
              <Tooltip
                title={item.tooltip}
                key={item.id}
                placement="bottom"
                enterDelay={400}
                classes={{
                  tooltip: "infra3d-buttonDescription",
                }}
              >
                <Button
                  disableRipple
                  key={item.id}
                  id={item.id}
                  onClick={() =>
                    monitor(
                      apm,
                      "activate-measure-tool | Measure",
                      () => activateTool(item),
                      { measureTool: item.id }
                    )
                  }
                  sx={{
                    width: "25%",
                    textAlign: "center",
                    borderRadius: "0px",
                    color: darkTheme ? "white" : "darkblue.main",
                    backgroundColor:
                      measurementActiveTool?.id === item.id
                        ? darkTheme
                          ? "darkblue.main"
                          : "white"
                        : "",
                  }}
                >
                  {measurementActiveTool?.id === item.id
                    ? item.activeIcon
                    : item.icon}
                  {measurementActiveTool?.id === item.id && (
                    <Divider
                      sx={{
                        position: "absolute",
                        bottom: "0",
                        left: "50%",
                        backgroundColor: "secondary.main",
                        borderRadius: "4px 4px 0px 0px",
                        height: "2px",
                        marginLeft: "-12px",
                        width: "24px",
                      }}
                    />
                  )}
                </Button>
              </Tooltip>
            );
          })}
        </Box>
        <Box
          sx={{
            marginTop: maximizeTable ? "-128px" : "0px",
            zIndex: 1,
            width: "100%",
            gap: 1,
            backgroundColor: darkTheme ? "darkblue.main" : "white",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "auto",
            transition: "margin 0.3s",
            position: "relative",
          }}
        >
          {measurementActiveTool !== undefined ? (
            <>
              <Box
                sx={{
                  height: "auto",
                  width: "100%",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    noWrap
                    sx={{
                      fontWeight: "700",
                      color: darkTheme ? "white" : "darkblue.main",
                    }}
                  >
                    {measurementActiveTool?.title}
                  </Typography>
                  {maximizeTable && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <IconButton
                        className="zoominout-button"
                        disableFocusRipple
                        disableRipple
                        disableTouchRipple
                        onClick={() =>
                          monitor(
                            apm,
                            "minimize-profile-tool | Measure",
                            () => {
                              changeShowDescription(false);
                              changeMaximizeTable(false);
                            }
                          )
                        }
                        sx={{
                          padding: 0,
                          color: darkTheme ? "white" : "darkblue.main",
                          "@media (hover: hover)": {
                            ":hover": { color: "secondary.main" },
                          },
                        }}
                      >
                        <ZoomInMap />
                      </IconButton>
                      <IconButton
                        sx={{
                          padding: 0,
                          marginY: 1,
                          color: darkTheme ? "white" : "darkblue.main",
                          "@media (hover: hover)": {
                            ":hover": { color: "secondary.main" },
                          },
                        }}
                        onClick={() =>
                          monitor(
                            apm,
                            "close-measure-tool | Measure",
                            handleCloseMeasurement
                          )
                        }
                        disableRipple={true}
                      >
                        <Close />
                      </IconButton>
                    </Box>
                  )}
                  {!maximizeTable && (
                    <IconButton
                      onClick={() =>
                        monitor(
                          apm,
                          "change-status-description | Measure",
                          handleDescription
                        )
                      }
                      disableRipple
                      size="small"
                      sx={{
                        rotate: showDescription ? "-180deg" : "0deg",
                        transition: "rotate 0.1s",
                        padding: 0,
                        color: darkTheme ? "white" : "darkblue.main",
                        "@media (hover: hover)": {
                          ":hover": { color: "secondary.main" },
                        },
                      }}
                    >
                      <ExpandMore />
                    </IconButton>
                  )}
                </Box>
                <Box
                  sx={{
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    variant="body2"
                    dangerouslySetInnerHTML={{
                      __html: measurementActiveTool?.description,
                    }}
                    sx={{
                      fontWeight: "400",
                      marginTop: showDescription ? "0%" : "-100%",
                      color: darkTheme ? "white" : "darkblue.main",
                      transition: "margin ease-in-out 0.2s",
                    }}
                  ></Typography>
                </Box>
              </Box>
              {canMeasure && measurementActiveTool?.isBlockedOnOldView ? (
                <Box
                  sx={{
                    width: "100%",
                    overflow: "auto",
                    height: "inherit",
                  }}
                >
                  <Typography
                    sx={{ color: darkTheme ? "white" : "darkblue.main" }}
                  >
                    {t("measurement.blocked_tool_message")}
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    overflow: "auto",
                    height: "inherit",
                  }}
                >
                  {measurementActiveTool?.content}
                </Box>
              )}
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <img
                src={measurement_empty_state}
                width="280px"
                alt="Designed by pch.vector / Magnific"
              />
              <Typography
                variant="body2"
                sx={{
                  textAlign: "center",
                  color: darkTheme ? "white" : "darkblue.main",
                }}
              >
                {t("measurement.empty_state_message")}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
